// Helpers del pincel inteligente (SAM2). Tipos de las marcas del usuario,
// llamada al endpoint de preview (sam2_mask → atlas /v1/images/mask con flag
// ON, ADR-601) y cálculo de créditos.
import { SAM2_MASK_URL, USE_ATLAS_BACKEND } from "../config";
import { GENERATE_AUTH_TOKEN } from "../config";
import { atlasBackendFetch } from "./atlasBackend";

// Una marca del usuario sobre el frame, en coords del FRAME (px del video real).
export interface EraseMark {
  // 'point' = tap (un click); 'scribble' = trazo (varios puntos arrastrados).
  type: "point" | "scribble";
  // include = lo que se quiere borrar (+); exclude = lo que NO (-).
  mode: "include" | "exclude";
  // puntos en px del frame. point => 1 punto; scribble => N puntos del trazo.
  points: Array<{ x: number; y: number }>;
}

// Tarifa de créditos por segundo de video borrado (misma que el eraser por
// máscara). AJUSTAR con el jefe; base Bria $0.14/s.
export const ERASE_CREDITS_PER_SEC = 1;

export function creditsForBrushErase(durationSec: number): number {
  return Math.ceil(Math.max(1, durationSec) * ERASE_CREDITS_PER_SEC);
}

// Convierte las marcas a la estructura que espera SAM2 (puntos con label 1/0).
// Un scribble se manda como varios puntos del mismo label.
export function marksToSamPoints(
  marks: EraseMark[],
): Array<{ x: number; y: number; label: 0 | 1 }> {
  const out: Array<{ x: number; y: number; label: 0 | 1 }> = [];
  for (const m of marks) {
    const label: 0 | 1 = m.mode === "include" ? 1 : 0;
    for (const p of m.points) out.push({ x: Math.round(p.x), y: Math.round(p.y), label });
  }
  return out;
}

// Pide a SAM2 (servidor) la máscara del frame para las marcas dadas.
// Devuelve un data URL PNG (máscara blanca sobre transparente) para overlay,
// o lanza si falla. `frameUrl` = imagen del frame subida/firmada.
export async function requestSam2Mask(opts: {
  frameUrl: string;
  marks: EraseMark[];
  companyId: string;
}): Promise<string> {
  const res = USE_ATLAS_BACKEND
    ? await atlasBackendFetch(
        "/v1/images/mask",
        {
          frame_url: opts.frameUrl,
          points: marksToSamPoints(opts.marks),
          // Trackeo (ADR-585): edición desde el Estudio. company_id ya no
          // viaja (sale del JWT).
          modo_utilizado: "estudio_edicion",
        },
        { signal: AbortSignal.timeout(60_000) },
      )
    : await fetch(SAM2_MASK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: GENERATE_AUTH_TOKEN },
        body: JSON.stringify({
          frame_url: opts.frameUrl,
          points: marksToSamPoints(opts.marks),
          company_id: opts.companyId,
          // Trackeo (ADR-585): edición desde el Estudio.
          modo_utilizado: "estudio_edicion",
        }),
        signal: AbortSignal.timeout(60_000),
      });
  // Leemos como texto primero para poder loguear el cuerpo real ante errores
  // (la respuesta puede venir vacía o no-JSON si n8n falló en algún nodo).
  const raw = await res.text();
  if (!res.ok) {
    console.error("[erase-brush][sam2_mask] HTTP", res.status, "→", raw.slice(0, 600));
    throw new Error(`SAM2 mask falló (HTTP ${res.status}). ${raw.slice(0, 200)}`);
  }
  let json: any;
  try {
    json = JSON.parse(raw);
  } catch {
    console.error(
      "[erase-brush][sam2_mask] respuesta NO-JSON (probable error en n8n):",
      JSON.stringify(raw.slice(0, 600)),
    );
    throw new Error(
      "El servidor devolvió una respuesta vacía/ inválida. Revisá la ejecución de 'sam2_mask' en n8n (algún nodo se puso rojo).",
    );
  }
  if (!json.mask_png) {
    console.error("[erase-brush][sam2_mask] sin mask_png:", JSON.stringify(json).slice(0, 400));
    throw new Error(json.error || "SAM2 no devolvió máscara (campo mask_png ausente).");
  }
  // n8n devuelve la máscara como data URL o base64; normalizamos a data URL.
  return json.mask_png.startsWith("data:")
    ? json.mask_png
    : `data:image/png;base64,${json.mask_png}`;
}
